import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getAirportName, getAirportCity } from "@/utils/airports";
import {
  Search, ExternalLink, Loader2, Zap, AlertTriangle, PlaneTakeoff,
  ChevronDown, ChevronUp, X, Plus, Plane, Briefcase, Timer, FileText,
  CheckCircle, Trash2, Share2, Pencil
} from "lucide-react";
import DealShareModal from "@/components/DealShareModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Shared helpers ───────────────────────────────────────────────────────────

function formatDuration(mins) {
  if (!mins) return "--";
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}
function formatPrice(price) {
  if (!price && price !== 0) return "--";
  return `€${Number(price).toLocaleString()}`;
}
function addDays(dateStr, days) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}
function parseDurMins(str) {
  if (!str) return null;
  const m = str.match(/(\d+)h\s*(\d+)m/);
  return m ? parseInt(m[1]) * 60 + parseInt(m[2]) : null;
}

// Duffel/Manual helpers
function calcConnectionTime(arriveAt, departAt) {
  if (!arriveAt || !departAt) return null;
  const diff = new Date(departAt) - new Date(arriveAt);
  if (diff <= 0 || diff > 24 * 3600000) return null;
  return `${Math.floor(diff / 3600000)}h ${Math.floor((diff % 3600000) / 60000)}m`;
}
function parseDuration(dur) {
  if (!dur) return "";
  const match = dur.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return dur;
  return ((match[1] ? `${match[1]}h` : "") + (match[2] ? ` ${match[2]}m` : "")).trim();
}
function durationToMinutes(dur) {
  if (!dur) return 99999;
  const match = dur.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!match) return 99999;
  return (parseInt(match[1] || 0) * 60) + parseInt(match[2] || 0);
}
function fmtTime(isoStr) {
  if (!isoStr) return "--:--";
  return new Date(isoStr).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}
function fmtDate(isoStr) {
  if (!isoStr) return "";
  return new Date(isoStr).toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });
}
function fmtShortDate(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "2-digit", month: "short" });
}

// Country-code options — value is the bare numeric calling code (no +)
const CALLING_CODES = [
  { value: "31",  label: "🇳🇱 +31" },
  { value: "44",  label: "🇬🇧 +44" },
  { value: "1",   label: "🇺🇸 +1"  },
  { value: "49",  label: "🇩🇪 +49" },
  { value: "32",  label: "🇧🇪 +32" },
  { value: "92",  label: "🇵🇰 +92" },
  { value: "971", label: "🇦🇪 +971" },
  { value: "974", label: "🇶🇦 +974" },
  { value: "966", label: "🇸🇦 +966" },
  { value: "90",  label: "🇹🇷 +90" },
  { value: "60",  label: "🇲🇾 +60" },
  { value: "91",  label: "🇮🇳 +91" },
];

/**
 * Strip formatting chars then remove any country-code prefix so only the
 * national number (no leading 0) is stored.
 * Returns { phone, country_code } — country_code may change if +CC auto-detected.
 */
function normalizePhone(raw, currentCode) {
  // Strip spaces, dashes, dots, parens
  let v = (raw || "").replace(/[\s\-\.\(\)]/g, "");
  let code = currentCode || "31";

  if (v.startsWith("+")) {
    // Try to match a known calling code
    const match = CALLING_CODES.find(c => v.startsWith("+" + c.value));
    if (match) {
      code = match.value;
      v = v.slice(1 + match.value.length);
    } else {
      v = v.slice(1); // strip + only
    }
  } else if (v.startsWith("00")) {
    const match = CALLING_CODES.find(c => v.startsWith("00" + c.value));
    if (match) {
      code = match.value;
      v = v.slice(2 + match.value.length);
    }
  }

  // Strip leading 0 (local-format number like 0648051678)
  if (v.startsWith("0")) v = v.slice(1);

  return { phone: v, country_code: code };
}
function isNextDay(depStr, arrStr) {
  if (!depStr || !arrStr) return false;
  const dep = new Date(depStr), arr = new Date(arrStr);
  return arr.getDate() !== dep.getDate() || arr.getMonth() !== dep.getMonth();
}
function getDealSegments(deal) {
  if (deal.segments && deal.segments.length > 0) return deal.segments;
  return [{ departure_airport: deal.departure_airport, arrival_airport: deal.arrival_airport, departure_time: deal.departure_time, arrival_time: deal.arrival_time, flight_number: deal.flight_number, operated_by: deal.airline }];
}
function getOfferSummary(offer) {
  const slices = offer.slices || [];
  const allSegments = slices.flatMap(s => s.segments || []);
  const totalStops = allSegments.length - slices.length;
  const airlines = new Set();
  allSegments.forEach(seg => { airlines.add(seg.marketing_carrier?.name || seg.operating_carrier?.name || ""); });
  const sliceSummaries = slices.map(slice => {
    const segs = slice.segments || [];
    const firstSeg = segs[0], lastSeg = segs[segs.length - 1];
    const layovers = [];
    for (let i = 1; i < segs.length; i++) {
      layovers.push({ duration: calcConnectionTime(segs[i-1].arriving_at, segs[i].departing_at), city: segs[i].origin?.iata_code || "" });
    }
    const dMin = durationToMinutes(slice.duration);
    return {
      depTime: fmtTime(firstSeg?.departing_at), arrTime: fmtTime(lastSeg?.arriving_at),
      nextDay: isNextDay(firstSeg?.departing_at, lastSeg?.arriving_at),
      duration: parseDuration(slice.duration), durationMin: dMin,
      origin: firstSeg?.origin?.iata_code || "", destination: lastSeg?.destination?.iata_code || "",
      stops: segs.length - 1, layovers,
      flightNums: segs.map(seg => (seg.marketing_carrier?.iata_code || "") + (seg.marketing_carrier_flight_number || "")).filter(Boolean)
    };
  });
  return {
    ownerName: offer.owner?.name || "Airline", ownerLogo: offer.owner?.logo_symbol_url, ownerIata: offer.owner?.iata_code || "",
    totalAmount: parseFloat(offer.total_amount), currency: offer.total_currency || "EUR",
    totalStops, airlines: [...airlines].filter(Boolean),
    sliceSummaries, totalDurationMin: sliceSummaries.reduce((a, s) => a + s.durationMin, 0)
  };
}

// ─── Carrier logos ────────────────────────────────────────────────────────────

const AIRLINE_COLORS = {
  EK: "#C41E3A", QR: "#5C0632", EY: "#B8860B", TK: "#E30A17",
  LH: "#05164D", BA: "#075AAA", KL: "#00A1DE", AF: "#002157",
  PC: "#FF6600", PK: "#006400", AC: "#F01428", WS: "#00827F",
  FZ: "#00C8FF", G9: "#FF6600", SV: "#006400", RJ: "#00843D",
  MS: "#C8A951", AI: "#E03A3E", U2: "#FF6600", FR: "#073590",
  IB: "#CC0000", VY: "#FAD000", LX: "#C00021", SK: "#003F87",
  AY: "#003580", OS: "#BD1924", SQ: "#F5A623", CX: "#006564",
  NH: "#003768", JL: "#E60012", MH: "#CC0001", TG: "#8B0000",
  UA: "#003087", AA: "#0078D2", DL: "#E01933",
};

const IATA_CORRECTIONS = {
  "TU": "TK",  // Tunis Air code → Turkish Airlines
  "EM": "EK",  // incorrect code → Emirates
  "QA": "QR",  // incorrect code → Qatar Airways
  "WE": "WS",  // incorrect code → WestJet
  "AL": "AC",  // incorrect code → Air Canada
};

function CarrierCircle({ code }) {
  const raw       = (code || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 2);
  const corrected = IATA_CORRECTIONS[raw] || raw;
  const [failed, setFailed] = useState(false);
  const color = AIRLINE_COLORS[corrected] || (
    "#" + corrected.split("").reduce((a, c) => (a * 31 + c.charCodeAt(0)) & 0xFFFFFF, 0)
      .toString(16).padStart(6, "0")
  );

  const logoUrl = `${process.env.REACT_APP_BACKEND_URL}/api/airlines/${corrected}/logo`;
  console.log("Logo proxy URL:", logoUrl);

  if (failed || !corrected) {
    return (
      <div style={{
        width: 44, height: 44, borderRadius: 8,
        background: color, display: "flex",
        alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>
        <span style={{ color: "white", fontWeight: 700, fontSize: 13, letterSpacing: 0.5 }}>
          {corrected || "??"}
        </span>
      </div>
    );
  }

  return (
    <div style={{
      width: 44, height: 44, borderRadius: 8,
      border: "1px solid #e5e7eb", background: "white",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, padding: 4,
    }}>
      <img
        src={logoUrl}
        alt={corrected}
        style={{ width: 36, height: 36, objectFit: "contain" }}
        onError={() => { console.log("Logo proxy failed for:", corrected); setFailed(true); }}
      />
    </div>
  );
}

// ─── UsageBadge ───────────────────────────────────────────────────────────────

function UsageBadge({ usage }) {
  if (!usage) return null;
  const { remaining, limit, exhausted, warning } = usage;
  if (exhausted) return <Badge className="bg-red-100 text-red-700 text-xs">Searches exhausted</Badge>;
  if (warning) return <Badge className="bg-amber-100 text-amber-700 text-xs">⚠ {remaining} left</Badge>;
  return <Badge className="bg-green-100 text-green-700 text-xs">{remaining}/{limit} remaining</Badge>;
}

// ─── DateSelector ─────────────────────────────────────────────────────────────

function DateSelector({ label, baseDate, selectedDate, onSelect }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2">
      <span className="text-xs text-gray-500 shrink-0 sm:w-28">{label}</span>
      <div className="flex items-center gap-1 flex-wrap">
        {[-3,-2,-1].map(d => {
          const dt = addDays(baseDate, d);
          return <button key={d} onClick={() => onSelect(dt)} className={`px-2 py-1 text-xs rounded border transition-colors ${selectedDate === dt ? "bg-purple-100 border-purple-400 text-purple-700 font-bold" : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}>{d}</button>;
        })}
        <button onClick={() => onSelect(baseDate)} className={`px-2 py-1 text-xs rounded border font-bold transition-colors ${selectedDate === baseDate ? "bg-purple-600 border-purple-600 text-white" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`}>0 ({baseDate})</button>
        {[1,2,3].map(d => {
          const dt = addDays(baseDate, d);
          return <button key={d} onClick={() => onSelect(dt)} className={`px-2 py-1 text-xs rounded border transition-colors ${selectedDate === dt ? "bg-purple-100 border-purple-400 text-purple-700 font-bold" : "border-gray-200 text-gray-500 hover:bg-gray-50"}`}>+{d}</button>;
        })}
      </div>
    </div>
  );
}

// ─── GoogleFlightsLink ────────────────────────────────────────────────────────

function GoogleFlightsLink({ origin, destination, date, returnDate, cabin }) {
  const cabinParam = cabin === "business" ? "c" : cabin === "first" ? "f" : "e";
  const trip = returnDate ? `${origin}.${destination}.${date}*${destination}.${origin}.${returnDate}` : `${origin}.${destination}.${date}`;
  const url = `https://www.google.com/flights#flt=${trip};c:${cabinParam};e:1;sd:1;t:f`;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer">
      <Button variant="outline" size="sm" className="text-blue-600 border-blue-200 hover:bg-blue-50 h-8">
        <ExternalLink className="w-3 h-3 mr-1" /> Google Flights ↗
      </Button>
    </a>
  );
}

// ─── buildLegsFromRequest ─────────────────────────────────────────────────────

function buildLegsFromRequest(request) {
  if (!request?.segments) return [];
  const travelType = request.travel_type || "one_way";
  if (travelType === "return") {
    const seg0 = request.segments[0];
    const segLast = request.segments[request.segments.length - 1];
    return [
      { origin: seg0.departure_airport, destination: seg0.arrival_airport, date: seg0.departure_date, label: "Outbound" },
      { origin: segLast.departure_airport, destination: segLast.arrival_airport, date: segLast.departure_date, label: "Return" },
    ];
  }
  return request.segments.map((seg, i) => ({
    origin: seg.departure_airport, destination: seg.arrival_airport, date: seg.departure_date,
    label: travelType === "multi_city" ? `Segment ${i + 1}` : "Outbound",
  }));
}

function mapSegToLeg(s) {
  return {
    departure_airport: s.departure_airport, arrival_airport: s.arrival_airport,
    departure_time: s.departure_time ? s.departure_time.replace("T", " ").slice(0, 16) : "--",
    arrival_time:   s.arrival_time   ? s.arrival_time.replace("T",   " ").slice(0, 16) : "--",
    airline: s.carrier, flight_number: s.flight_number, duration: parseDurMins(s.duration),
  };
}

// ─── FilterBar ────────────────────────────────────────────────────────────────

function FilterBar({ filters, onChange, airlines = [], transitAirports = [], showTransit = false }) {
  const { stops, maxPrice, sort, selectedAirlines } = filters;
  const transitSet = filters.transitAirports || new Set();
  const hasActive = stops !== "any" || maxPrice || selectedAirlines.size > 0 || sort !== "cheapest" || transitSet.size > 0;
  const resetFilters = () => onChange({ stops: "any", maxPrice: null, sort: "cheapest", selectedAirlines: new Set(), transitAirports: new Set() });

  return (
    <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex flex-col gap-1.5">
      {/* Row 1: Stops · Max Price · Sort · Reset */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 font-medium mr-0.5">Stops:</span>
          {[["any","All"],["0","Direct"],["1","1"],["2+","2+"]].map(([val, label]) => (
            <button key={val} onClick={() => onChange({ ...filters, stops: val })}
              className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${stops === val ? "bg-purple-600 text-white border-purple-600" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 font-medium">Max €</span>
          <input type="number" placeholder="Any" value={maxPrice || ""}
            onChange={e => onChange({ ...filters, maxPrice: e.target.value ? Number(e.target.value) : null })}
            className="w-16 text-[11px] border border-gray-200 rounded-md px-1.5 py-0.5 focus:outline-none focus:ring-1 focus:ring-purple-400 bg-white" />
          {maxPrice && <button onClick={() => onChange({ ...filters, maxPrice: null })} className="text-gray-300 hover:text-gray-500"><X className="w-3 h-3" /></button>}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-[11px] text-gray-500 font-medium mr-0.5">Sort:</span>
          {[["cheapest","Cheapest"],["fastest","Fastest"],["earliest","Earliest"],["latest","Latest"]].map(([val, label]) => (
            <button key={val} onClick={() => onChange({ ...filters, sort: val })}
              className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${sort === val ? "bg-purple-600 text-white border-purple-600" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}>
              {label}
            </button>
          ))}
        </div>
        {hasActive && (
          <button onClick={resetFilters} className="text-[11px] text-purple-600 hover:underline ml-auto">Reset</button>
        )}
      </div>

      {/* Row 2: Airlines pills */}
      {airlines.length > 1 && (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[11px] text-gray-500 font-medium mr-0.5">Airlines:</span>
          <button onClick={() => onChange({ ...filters, selectedAirlines: new Set() })}
            className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${selectedAirlines.size === 0 ? "bg-purple-600 text-white border-purple-600" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}>
            All
          </button>
          {airlines.map(a => (
            <button key={a} onClick={() => {
              const next = new Set(selectedAirlines);
              if (next.has(a)) next.delete(a); else next.add(a);
              onChange({ ...filters, selectedAirlines: next });
            }}
              className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${selectedAirlines.has(a) ? "bg-purple-600 text-white border-purple-600" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}>
              {a}
            </button>
          ))}
        </div>
      )}

      {/* Row 3: Transit via (Google only) */}
      {showTransit && transitAirports.length > 0 && (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[11px] text-gray-500 font-medium mr-0.5">Via:</span>
          <button onClick={() => onChange({ ...filters, transitAirports: new Set() })}
            className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors ${transitSet.size === 0 ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}>
            Any
          </button>
          {transitAirports.map(a => (
            <button key={a} onClick={() => {
              const next = new Set(transitSet);
              if (next.has(a)) next.delete(a); else next.add(a);
              onChange({ ...filters, transitAirports: next });
            }}
              className={`px-2 py-0.5 text-[11px] rounded-full border transition-colors font-mono ${transitSet.has(a) ? "bg-blue-600 text-white border-blue-600" : "border-gray-300 text-gray-600 hover:bg-gray-100"}`}>
              {a}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── SegmentList ──────────────────────────────────────────────────────────────

function SegmentList({ segments, label }) {
  if (!segments?.length) return null;
  return (
    <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
      {label && <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">{label}</p>}
      {segments.map((s, j) => (
        <div key={j} className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
          <span className="font-mono">{s.departure_airport}</span><span>→</span><span className="font-mono">{s.arrival_airport}</span>
          <span className="text-gray-300">·</span><span>{s.flight_number}</span>
          {s.duration && <><span className="text-gray-300">·</span><span>{s.duration}</span></>}
        </div>
      ))}
    </div>
  );
}

// ─── FareAmenityPill ──────────────────────────────────────────────────────────

function FareAmenityPill({ label, value }) {
  const free = value && !value.toLowerCase().includes("charge") && value !== "No bag";
  return (
    <div className="flex flex-col items-center gap-0.5">
      <span className="text-[9px] text-gray-400 uppercase tracking-wide">{label}</span>
      <span className={`text-[10px] font-medium ${free ? "text-green-600" : "text-gray-400"}`}>{value || "—"}</span>
    </div>
  );
}

// ─── SourceStatus ─────────────────────────────────────────────────────────────

function SourceStatus({ label, loading, count, color }) {
  if (loading) return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-${color}-50 border border-${color}-200 text-${color}-600`}>
      <Loader2 className="w-3 h-3 animate-spin" />{label}
    </span>
  );
  if (count > 0) return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-${color}-100 border border-${color}-300 text-${color}-700 font-medium`}>
      ✓ {label} ({count})
    </span>
  );
  return null;
}

// ─── AmadeusResultCard ────────────────────────────────────────────────────────

function AmadeusResultCard({ result, index, onNewDeal, request }) {
  const { getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const fareOptions  = result.fare_options || [];
  const [activeFare, setActiveFare] = useState(0);
  const [fareRulesOpen, setFareRulesOpen] = useState(false);
  const selectedFare   = fareOptions[activeFare] || {};
  const activeRawOffer = selectedFare.raw_offer || result.raw_offer;
  const activePrice    = selectedFare.price    ?? result.price;

  const outSegs     = result.segments || [];
  const retSegs     = result.return_segments || [];
  const allSegs     = [...outSegs, ...retSegs];
  const firstSeg    = outSegs[0] || {};
  const lastOutSeg  = outSegs[outSegs.length - 1] || {};
  const firstRetSeg = retSegs[0] || {};
  const lastRetSeg  = retSegs[retSegs.length - 1] || {};
  const fmt = t => t ? t.replace("T", " ").slice(0, 16) : "--";

  const [priceModal,     setPriceModal]     = useState(false);
  const [passengerModal, setPassengerModal] = useState(false);
  const [successModal,   setSuccessModal]   = useState(false);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [confirmedPrice, setConfirmedPrice] = useState(null);
  const [confirmedCurrency, setConfirmedCurrency] = useState("EUR");
  const [confirmedRawOffer, setConfirmedRawOffer] = useState(null);
  const [successData, setSuccessData]       = useState(null);

  const reqAdults   = request?.passengers?.adults   || 1;
  const reqChildren = request?.passengers?.children || 0;
  const reqInfants  = request?.passengers?.infants  || 0;
  const numPassengers = reqAdults + reqChildren + reqInfants;

  const buildInitPassengers = () => {
    const pax = [];
    for (let i = 0; i < reqAdults; i++) {
      const { phone: seedPhone, country_code: seedCode } = i === 0
        ? normalizePhone(request?.phone || "", "31")
        : { phone: "", country_code: "31" };
      pax.push({ title: "Mr", first_name: "", last_name: "", email: i === 0 ? (request?.email || "") : "", phone: seedPhone, date_of_birth: "", gender: "Male", passenger_type: "adult", country_code: seedCode });
    }
    for (let i = 0; i < reqChildren; i++)
      pax.push({ title: "Mstr", first_name: "", last_name: "", email: "", phone: "", date_of_birth: "", gender: "Male", passenger_type: "child", country_code: "31" });
    for (let i = 0; i < reqInfants; i++)
      pax.push({ title: "Mstr", first_name: "", last_name: "", email: "", phone: "", date_of_birth: "", gender: "Male", passenger_type: "infant", country_code: "31" });
    return pax;
  };

  const [passengers, setPassengers]     = useState(buildInitPassengers);
  const [sameContact, setSameContact]   = useState(false);
  const [sellingPrice, setSellingPrice] = useState("");
  const [notes, setNotes]               = useState("");

  const handleNewDeal = () => {
    if (!onNewDeal) return;
    onNewDeal({
      price: activePrice, stops: result.stops,
      departure_airport: firstSeg.departure_airport, arrival_airport: lastOutSeg.arrival_airport,
      departure_time: fmt(firstSeg.departure_time), arrival_time: fmt(lastOutSeg.arrival_time),
      airline: result.validating_airline || firstSeg.carrier, flight_number: firstSeg.flight_number,
      travel_class: result.cabin_class, legs: allSegs.map(mapSegToLeg),
    });
  };

  const handleBookAmadeus = async () => {
    setPricingLoading(true);
    try {
      const res = await axios.post(`${API}/flights/price-amadeus`, { raw_offer: activeRawOffer }, { headers: getAuthHeaders() });
      setConfirmedPrice(res.data.confirmed_price);
      setConfirmedCurrency(res.data.currency || "EUR");
      setConfirmedRawOffer(res.data.raw_offer);
      setSellingPrice(String(res.data.confirmed_price));
      setPassengers(buildInitPassengers());
      setSameContact(false);
      setNotes("");
      setPriceModal(true);
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      if (detail.includes("No fare applicable") || detail.includes("INVALID DATA"))
        toast.error("This fare is no longer bookable via Amadeus. Please choose another option.");
      else
        toast.error(typeof detail === "string" && detail ? detail : "Repricing failed. Please try again.");
    } finally { setPricingLoading(false); }
  };

  const updatePassenger = (i, field, rawValue) =>
    setPassengers(prev => {
      const base = prev[i];
      let changes = { [field]: rawValue };

      if (field === "phone") {
        // Normalize: auto-detect +CC, strip leading 0
        const { phone, country_code } = normalizePhone(rawValue, base.country_code);
        changes = { phone, country_code };
      } else if (field === "country_code") {
        // When country code changes, re-strip it from the stored phone in case
        // user had previously typed a full international number
        const { phone } = normalizePhone(base.phone, rawValue);
        changes = { country_code: rawValue, phone };
      }

      const updated = prev.map((p, idx) => idx === i ? { ...p, ...changes } : p);
      if (sameContact && i === 0 && ["email", "phone", "country_code"].includes(field))
        return updated.map((p, idx) =>
          idx === 0 ? p : { ...p, phone: changes.phone ?? p.phone, country_code: changes.country_code ?? p.country_code, ...(field === "email" ? { email: rawValue } : {}) }
        );
      return updated;
    });

  const validatePassengerForm = () => {
    const today = new Date();
    const getAge = (dob) => dob ? (today - new Date(dob)) / (1000 * 60 * 60 * 24 * 365.25) : null;
    const errors = [];
    passengers.forEach((p, i) => {
      const typeLabel = p.passenger_type === "child" ? "Child" : p.passenger_type === "infant" ? "Infant" : "Adult";
      const label = `${typeLabel} ${i + 1}`;
      if (!p.first_name.trim()) errors.push(`${label}: First name required`);
      if (!p.last_name.trim()) errors.push(`${label}: Last name required`);
      if (!p.date_of_birth) errors.push(`${label}: Date of birth required`);
      const age = getAge(p.date_of_birth);
      if (age !== null) {
        if (p.passenger_type === "adult" && age < 12)
          errors.push(`${label}: DOB gives age ${Math.floor(age)} — adult must be 12+`);
        if (p.passenger_type === "child" && (age < 2 || age >= 12))
          errors.push(`${label}: DOB gives age ${Math.floor(age)} — child must be 2–11`);
        if (p.passenger_type === "infant" && age >= 2)
          errors.push(`${label}: DOB gives age ${Math.floor(age)} — infant must be under 2`);
      }
      if (p.passenger_type !== "infant") {
        if (!p.email?.trim()) errors.push(`${label}: Email required`);
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(p.email.trim()))
          errors.push(`${label}: Invalid email`);
        const trimPhone = p.phone?.trim() || "";
        if (!trimPhone) {
          errors.push(`${label}: Phone required`);
        } else if (/[+]/.test(trimPhone) || trimPhone.startsWith(p.country_code)) {
          errors.push(`${label}: Enter phone without country code (e.g. 648051678)`);
        } else if (trimPhone.replace(/\D/g, "").length < 6) {
          errors.push(`${label}: Phone number too short`);
        }
      }
    });
    return errors;
  };

  const handleCompleteBooking = async () => {
    const errors = validatePassengerForm();
    if (errors.length > 0) { toast.error(errors[0]); return; }
    setBookingLoading(true);
    try {
      const res = await axios.post(`${API}/flights/book-amadeus`, {
        request_id: request?.id || null,
        raw_offer: confirmedRawOffer || activeRawOffer,
        passengers,
        selling_price: parseFloat(sellingPrice) || confirmedPrice,
        notes,
      }, { headers: getAuthHeaders() });
      setSuccessData(res.data);
      setPassengerModal(false);
      setSuccessModal(true);
      setTimeout(() => { setSuccessModal(false); navigate(`/bookings/${res.data.booking_id}`); }, 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      let msg = "Booking failed";
      if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(d => d.msg || JSON.stringify(d)).join("; ");
      } else if (detail && typeof detail === "object") {
        // Amadeus error object: { amadeus_status, amadeus_response: { errors: [...] } }
        const amResp = detail.amadeus_response;
        const errors = amResp?.errors || (Array.isArray(amResp) ? amResp : null);
        if (errors?.length) {
          msg = errors.map(e => [e.title, e.detail].filter(Boolean).join(" — ")).join("; ")
               || `Amadeus error ${detail.amadeus_status}`;
        } else if (detail.amadeus_status) {
          msg = `Amadeus error ${detail.amadeus_status}: ${JSON.stringify(amResp).slice(0, 200)}`;
        } else {
          msg = JSON.stringify(detail).slice(0, 200);
        }
      }
      toast.error(msg, { duration: 8000 });
    } finally { setBookingLoading(false); }
  };

  const searchPrice = Number(activePrice);
  const priceChanged = confirmedPrice !== null && Math.abs(confirmedPrice - searchPrice) > 1;
  const hasFares = fareOptions.length > 1;

  return (
    <>
      <div className={`rounded-xl border shadow-sm transition-all overflow-hidden ${index === 0 ? "border-purple-300" : "border-gray-100 hover:border-purple-200 hover:shadow-md"}`}>
        <div className={`p-4 flex items-start gap-3 ${index === 0 ? "bg-purple-50/60" : "bg-white"}`}>
          <CarrierCircle code={result.validating_iata || activeRawOffer?.validatingAirlineCodes?.[0] || firstSeg.carrier} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <span className="font-mono text-sm font-bold text-gray-800">{firstSeg.departure_airport}</span>
              <span className="text-gray-300">→</span>
              <span className="font-mono text-sm font-bold text-gray-800">{lastOutSeg.arrival_airport}</span>
              {result.is_return && (
                <><span className="text-gray-300 mx-0.5">⇄</span><span className="font-mono text-sm font-bold text-gray-800">{lastRetSeg.arrival_airport}</span></>
              )}
              {index === 0 && <Badge className="bg-purple-100 text-purple-700 text-[10px] px-1.5">Cheapest</Badge>}
            </div>
            <div className="text-xs text-gray-500">
              {result.validating_airline || firstSeg.carrier} · {firstSeg.flight_number}
              {result.stops === 0 ? " · Direct" : ` · ${result.stops} stop${result.stops > 1 ? "s" : ""}`}
              {result.total_duration ? ` · ${result.total_duration}` : ""}
              {result.is_return && result.return_duration ? ` | return ${result.return_duration}` : ""}
            </div>
            <div className="text-xs text-gray-400 mt-0.5">
              {fmt(firstSeg.departure_time)} → {fmt(lastOutSeg.arrival_time)}
              {result.is_return && firstRetSeg.departure_time && (
                <span className="ml-3 text-purple-400">↩ {fmt(firstRetSeg.departure_time)} → {fmt(lastRetSeg.arrival_time)}</span>
              )}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className={`text-xl font-bold ${index === 0 ? "text-purple-700" : "text-gray-800"}`}>{formatPrice(activePrice)}</div>
            {result.is_return && <div className="text-[10px] text-gray-400 -mt-0.5">round trip</div>}
            <div className="flex flex-col gap-1 mt-2 items-end">
              <Button size="sm" disabled={pricingLoading} onClick={handleBookAmadeus} className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-7 px-3">
                {pricingLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : "Book via Amadeus"}
              </Button>
              <button className="text-[10px] text-gray-400 hover:text-purple-600 hover:underline whitespace-nowrap" onClick={handleNewDeal}>New deal →</button>
            </div>
          </div>
        </div>
        {(outSegs.length > 1 || retSegs.length > 0) && (
          <div className="px-4 pb-2 bg-white border-t border-gray-50">
            <SegmentList segments={outSegs.length > 1 ? outSegs : []} label={result.is_return ? "Outbound" : null} />
            <SegmentList segments={retSegs} label={result.is_return ? "Return" : null} />
          </div>
        )}
        {hasFares && (
          <div className="border-t border-gray-100 bg-gray-50">
            <div className="flex overflow-x-auto">
              {fareOptions.map((fare, fi) => (
                <button key={fi} onClick={() => setActiveFare(fi)}
                  className={`flex-1 min-w-[90px] px-3 py-2 text-center transition-colors border-b-2 ${activeFare === fi ? "border-purple-600 bg-white" : "border-transparent hover:bg-gray-100"}`}>
                  <div className={`text-[10px] font-semibold uppercase tracking-wide truncate ${activeFare === fi ? "text-purple-700" : "text-gray-500"}`}>{fare.fare_name}</div>
                  <div className={`text-sm font-bold mt-0.5 ${activeFare === fi ? "text-purple-700" : "text-gray-700"}`}>{formatPrice(fare.price)}</div>
                </button>
              ))}
            </div>
          </div>
        )}
        {selectedFare.baggage !== undefined && (
          <div className="flex items-center gap-4 px-4 py-2 bg-white border-t border-gray-100 flex-wrap">
            <FareAmenityPill label="Checked bag" value={selectedFare.baggage} />
            {selectedFare.amenities?.seat    && <FareAmenityPill label="Seat"    value={selectedFare.amenities.seat} />}
            {selectedFare.amenities?.changes && <FareAmenityPill label="Changes" value={selectedFare.amenities.changes} />}
            {selectedFare.amenities?.refund  && <FareAmenityPill label="Refund"  value={selectedFare.amenities.refund} />}
            <button onClick={(e) => { e.stopPropagation(); setFareRulesOpen(true); }} className="text-[10px] text-purple-600 border border-purple-200 rounded px-2 py-0.5 hover:bg-purple-50 ml-auto">Fare Rules</button>
          </div>
        )}
      </div>

      {/* Step 1: Reprice Modal */}
      <Dialog open={priceModal} onOpenChange={setPriceModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Confirm Amadeus Price</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 text-sm">
              <div className="flex items-center gap-1 font-mono font-bold text-purple-800 mb-1">
                <span>{firstSeg.departure_airport}</span><span className="text-gray-400">→</span><span>{lastOutSeg.arrival_airport}</span>
                {result.is_return && <><span className="text-gray-300 mx-1">⇄</span><span>{lastRetSeg.arrival_airport}</span></>}
              </div>
              <div className="text-xs text-gray-600">{result.validating_airline || firstSeg.carrier} · {firstSeg.flight_number}{result.stops === 0 ? " · Direct" : ` · ${result.stops} stop${result.stops > 1 ? "s" : ""}`}</div>
              <div className="text-xs text-gray-500 mt-0.5">{fmt(firstSeg.departure_time)} → {fmt(lastOutSeg.arrival_time)}</div>
            </div>
            <div className="text-center">
              <p className="text-sm text-gray-500 mb-1">Confirmed Price</p>
              <p className="text-3xl font-bold text-purple-700">{confirmedCurrency === "EUR" ? "€" : confirmedCurrency}{confirmedPrice !== null ? Number(confirmedPrice).toLocaleString() : "--"}</p>
            </div>
            {priceChanged && (
              <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-700">
                <AlertTriangle className="w-3 h-3 shrink-0" />
                Price changed from €{searchPrice.toLocaleString()} to €{Number(confirmedPrice).toLocaleString()}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPriceModal(false)}>Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={() => { setPriceModal(false); setPassengerModal(true); }}>Confirm &amp; Book</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 2: Passenger Form */}
      <Dialog open={passengerModal} onOpenChange={setPassengerModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Passenger Details</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            {passengers.length > 1 && (
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                <input type="checkbox" checked={sameContact} onChange={e => {
                  const checked = e.target.checked;
                  setSameContact(checked);
                  if (checked && passengers.length > 1) {
                    const { email, phone, country_code } = passengers[0];
                    setPassengers(prev => prev.map((p, i) => i === 0 ? p : { ...p, email, phone, country_code }));
                  }
                }} className="rounded" />
                Use same contact details for all passengers
              </label>
            )}
            {passengers.map((pax, i) => {
              const typeLabel = pax.passenger_type === "child" ? "Child" : pax.passenger_type === "infant" ? "Infant" : "Adult";
              const typeBg = pax.passenger_type === "infant" ? "bg-amber-50 border-amber-200" : pax.passenger_type === "child" ? "bg-blue-50 border-blue-200" : "bg-white border-gray-200";
              const dobHint = pax.passenger_type === "infant" ? "Must be under 2 years" : pax.passenger_type === "child" ? "Must be 2–11 years" : "Must be 12+ years";
              const isContactDisabled = sameContact && i > 0;
              return (
                <div key={i} className={`p-3 border rounded-lg space-y-3 ${typeBg}`}>
                  <div className="flex items-center gap-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Passenger {i + 1}</p>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${pax.passenger_type === "infant" ? "bg-amber-200 text-amber-800" : pax.passenger_type === "child" ? "bg-blue-200 text-blue-800" : "bg-purple-100 text-purple-700"}`}>{typeLabel}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Title</Label>
                      <select value={pax.title} onChange={e => updatePassenger(i, "title", e.target.value)} className="w-full border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400">
                        <option>Mr</option><option>Mrs</option><option>Ms</option><option>Miss</option><option>Mstr</option><option>Dr</option>
                      </select>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">Gender</Label>
                      <select value={pax.gender} onChange={e => updatePassenger(i, "gender", e.target.value)} className="w-full border rounded-md px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400">
                        <option>Male</option><option>Female</option>
                      </select>
                    </div>
                    <div className="space-y-1"><Label className="text-xs">First Name</Label><Input value={pax.first_name} onChange={e => updatePassenger(i, "first_name", e.target.value)} placeholder="First name" /></div>
                    <div className="space-y-1"><Label className="text-xs">Last Name</Label><Input value={pax.last_name} onChange={e => updatePassenger(i, "last_name", e.target.value)} placeholder="Last name" /></div>
                    <div className="space-y-1"><Label className="text-xs">Email</Label><Input type="email" value={pax.email} onChange={e => updatePassenger(i, "email", e.target.value)} placeholder="email@example.com" disabled={isContactDisabled} className={isContactDisabled ? "opacity-60" : ""} /></div>
                    <div className="space-y-1">
                      <Label className="text-xs">Phone</Label>
                      <div className="flex gap-1">
                        <select value={pax.country_code} onChange={e => updatePassenger(i, "country_code", e.target.value)} disabled={isContactDisabled} className={`border rounded-md px-1 py-1.5 text-xs w-[86px] focus:outline-none focus:ring-2 focus:ring-purple-400 ${isContactDisabled ? "opacity-60" : ""}`}>
                          {CALLING_CODES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                        </select>
                        <Input
                          value={pax.phone}
                          onChange={e => updatePassenger(i, "phone", e.target.value)}
                          placeholder="648051678"
                          disabled={isContactDisabled}
                          className={`flex-1 ${isContactDisabled ? "opacity-60" : ""}`}
                        />
                      </div>
                    </div>
                    <div className="space-y-1 col-span-2">
                      <Label className="text-xs">Date of Birth <span className="text-gray-400 font-normal normal-case">({dobHint})</span></Label>
                      <Input type="date" value={pax.date_of_birth} onChange={e => updatePassenger(i, "date_of_birth", e.target.value)} />
                    </div>
                  </div>
                </div>
              );
            })}
            <div className="border-t pt-3 space-y-3">
              <div className="space-y-1"><Label className="text-xs">Selling Price (€)</Label><Input type="number" value={sellingPrice} onChange={e => setSellingPrice(e.target.value)} /></div>
              <div className="space-y-1"><Label className="text-xs">Notes (optional)</Label><textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Any special requests..." className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none" /></div>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPassengerModal(false)}>Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" disabled={bookingLoading} onClick={handleCompleteBooking}>
              {bookingLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Booking...</> : "Complete Booking"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Step 3: Success */}
      <Dialog open={successModal} onOpenChange={setSuccessModal}>
        <DialogContent className="max-w-sm text-center">
          <DialogHeader><DialogTitle className="text-green-700">Booking Confirmed!</DialogTitle></DialogHeader>
          <div className="py-4 space-y-2">
            <div className="text-4xl">✓</div>
            {successData && (<><p className="text-lg font-bold font-mono text-green-700">{successData.pnr}</p><p className="text-sm text-gray-500">Booking ID: {successData.booking_id}</p></>)}
            <p className="text-xs text-gray-400 mt-2">Redirecting to booking detail...</p>
          </div>
        </DialogContent>
      </Dialog>

      <FareConditionsModal
        open={fareRulesOpen}
        onClose={() => setFareRulesOpen(false)}
        title={selectedFare.fare_name || "Fare"}
        route={`${firstSeg.departure_airport || ""} → ${lastOutSeg.arrival_airport || ""}`}
        fareData={buildAmadeusConditions(selectedFare)}
      />
    </>
  );
}

// ─── GoogleFlightCard ─────────────────────────────────────────────────────────

function GoogleFlightCard({ flight, index, onNewDeal, onAddLeg }) {
  return (
    <div className={`p-4 rounded-xl border shadow-sm transition-all ${index === 0 ? "border-blue-300 bg-blue-50/60" : "border-gray-100 bg-white hover:border-blue-200 hover:shadow-md"}`}>
      <div className="flex items-start gap-3">
        <CarrierCircle code={flight.flight_number?.match(/^[A-Z]{2}/)?.[0] || flight.airline?.slice(0, 2)} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            <span className="font-mono text-sm font-bold text-gray-800">{flight.departure_airport}</span>
            <span className="text-gray-300">→</span>
            <span className="font-mono text-sm font-bold text-gray-800">{flight.arrival_airport}</span>
            {index === 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px] px-1.5">Cheapest</Badge>}
          </div>
          <div className="text-xs text-gray-500">
            {flight.airline} · {flight.flight_number}
            {flight.stops === 0 ? " · Direct" : ` · ${flight.stops} stop${flight.stops > 1 ? "s" : ""}`}
            {flight.total_duration ? ` · ${formatDuration(flight.total_duration)}` : ""}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">{flight.departure_time} → {flight.arrival_time}</div>
        </div>
        <div className="text-right shrink-0">
          <div className={`text-xl font-bold ${index === 0 ? "text-blue-700" : "text-gray-800"}`}>{formatPrice(flight.price)}</div>
          <div className="flex flex-col gap-1 mt-2 items-end">
            <button className="text-[10px] text-blue-600 hover:underline whitespace-nowrap" onClick={() => onNewDeal && onNewDeal(flight)}>New deal →</button>
            {onAddLeg && <button className="text-[10px] text-purple-500 hover:underline whitespace-nowrap" onClick={() => onAddLeg(flight)}>+ Add leg</button>}
          </div>
        </div>
      </div>
      {flight.legs?.length > 1 && (
        <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
          {flight.legs.map((leg, j) => (
            <div key={j} className="text-xs text-gray-500 flex items-center gap-1 flex-wrap">
              <span className="font-mono">{leg.departure_airport}</span><span>→</span><span className="font-mono">{leg.arrival_airport}</span>
              <span className="text-gray-300">·</span><span>{leg.flight_number}</span>
              <span className="text-gray-300">·</span><span>{leg.departure_time} → {leg.arrival_time}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── FareConditionsModal ──────────────────────────────────────────────────────

function FareConditionsModal({ open, onClose, title, route, fareData, loading }) {
  const CondRow = ({ label, item }) => {
    if (!item) return null;
    const color = item.ok ? "text-green-600" : "text-red-500";
    const icon  = item.ok ? "✓" : "✗";
    return (
      <div className="flex items-start gap-1.5 py-0.5 text-xs">
        <span className={`font-bold shrink-0 mt-0.5 w-3 text-center ${color}`}>{icon}</span>
        <span className="text-gray-500 shrink-0 w-28">{label}</span>
        <span className={`font-medium ${color}`}>{item.label}</span>
      </div>
    );
  };

  const Section = ({ icon, heading, children }) => {
    const rows = Array.isArray(children) ? children : [children];
    const hasContent = rows.some(r => r && r.props?.item);
    if (!hasContent) return null;
    return (
      <div className="mb-3 last:mb-0">
        <p className="text-[11px] font-semibold text-gray-600 mb-1 flex items-center gap-1">{icon} {heading}</p>
        {children}
      </div>
    );
  };

  const hasData = fareData && fareData.hasAmenities;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-base">Fare Conditions — {title}</DialogTitle>
          {route && <p className="text-xs text-gray-500 mt-0.5">{route}</p>}
          {fareData?.fareBasis && (
            <p className="text-xs text-gray-400">
              Fare basis: <span className="font-mono">{fareData.fareBasis}</span>
              {fareData.cabin ? ` · ${fareData.cabin}` : ""}
            </p>
          )}
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center">
            <Loader2 className="w-6 h-6 mx-auto animate-spin text-purple-400" />
            <p className="text-xs text-gray-400 mt-2">Loading fare conditions…</p>
          </div>
        ) : !hasData ? (
          <div className="py-6 text-center">
            <p className="text-xs text-gray-400">Fare conditions not available for this fare.</p>
            <p className="text-xs text-gray-400 mt-0.5">Please check with the airline directly.</p>
          </div>
        ) : (
          <div className="py-2 grid grid-cols-1 sm:grid-cols-2 gap-x-6">
            <div>
              <Section icon="🧳" heading="Baggage">
                <CondRow label="Checked bags" item={fareData.baggage?.checked} />
                <CondRow label="Carry-on" item={fareData.baggage?.carryOn} />
                <CondRow label="Personal item" item={fareData.baggage?.personal} />
              </Section>
            </div>
            <div>
              <Section icon="✏️" heading="Changes">
                <CondRow label="Before departure" item={fareData.changes?.beforeDeparture} />
                <CondRow label="No-show" item={fareData.changes?.noShow} />
              </Section>
              <Section icon="❌" heading="Cancellation">
                <CondRow label="Before departure" item={fareData.cancellation?.beforeDeparture} />
              </Section>
              <Section icon="💺" heading="Seat Selection">
                <CondRow label="Standard seat" item={fareData.seats?.standard} />
                <CondRow label="Extra legroom" item={fareData.seats?.extraLegroom} />
              </Section>
            </div>
          </div>
        )}

        <div className="border-t pt-2 space-y-0.5">
          {fareData?.gdsDisclaimer && (
            <p className="text-[10px] text-amber-600">⚠ Exact fee amounts are not available via GDS. Check the airline website or contact us for detailed fare conditions before booking.</p>
          )}
          <p className="text-[10px] text-gray-400">Always verify conditions directly with the airline before booking.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function buildAmadeusConditions(fare) {
  const raw = fare?.raw_offer;
  const tp  = raw?.travelerPricings?.[0];
  const fd0 = tp?.fareDetailsBySegment?.[0];
  const amenitiesArr = fd0?.amenities || [];

  const baggage      = {};
  const changes      = {};
  const cancellation = {};
  const seats        = {};
  let gdsDisclaimer  = false;

  // Checked baggage from includedCheckedBags
  const incl = fd0?.includedCheckedBags || {};
  if (incl.weight && incl.weight > 0) {
    baggage.checked = { ok: true, label: `${incl.weight}${(incl.weightUnit || "kg").toLowerCase()} included` };
  } else if (incl.quantity > 0) {
    baggage.checked = { ok: true, label: `${incl.quantity} bag${incl.quantity > 1 ? "s" : ""} included` };
  } else {
    baggage.checked = { ok: false, label: "Not included" };
  }

  let hasAmenities = amenitiesArr.length > 0;
  for (const a of amenitiesArr) {
    const chargeable = a.isChargeable !== false;
    const desc    = (a.description || "").trim();
    const atype   = (a.amenityType || "").toUpperCase();
    const descUp  = desc.toUpperCase();

    if (atype === "BAGGAGE") {
      if (/carry|cabin|hand/i.test(desc)) {
        baggage.carryOn = { ok: !chargeable, label: chargeable ? "Paid" : (desc || "Included") };
      } else if (/personal|pouch/i.test(desc)) {
        baggage.personal = { ok: !chargeable, label: chargeable ? "Paid" : (desc || "Included") };
      }

    } else if (atype === "PRE_RESERVED_SEAT" || /^seats?$/.test(atype)) {
      if (/extra|legroom|premium/i.test(desc)) {
        seats.extraLegroom = { ok: !chargeable, label: chargeable ? "Paid" : "Free" };
      } else {
        seats.standard = { ok: !chargeable, label: chargeable ? "Paid" : "Free" };
      }

    } else if (
      atype.includes("CHANGE") || atype === "RULE_OVERRIDE" ||
      descUp.includes("REISSUE") || descUp.includes("CHANGE")
    ) {
      if (!changes.beforeDeparture) {
        let label;
        if (descUp.includes("NON CHANGEABLE") || descUp.includes("NON-CHANGEABLE")) {
          label = "Not permitted";
          changes.beforeDeparture = { ok: false, label };
        } else if (descUp.includes("FREE CHANGE") || descUp.includes("NO CHANGE FEE") || !chargeable) {
          label = "Free";
          changes.beforeDeparture = { ok: true, label };
        } else if (descUp.includes("CHANGEABLE")) {
          label = "Permitted (fee unknown)";
          changes.beforeDeparture = { ok: true, label };
          gdsDisclaimer = true;
        } else {
          label = "Fee applies (check airline)";
          changes.beforeDeparture = { ok: false, label };
          gdsDisclaimer = true;
        }
      }
      if (descUp.includes("NO-SHOW") || descUp.includes("NOSHOW")) {
        changes.noShow = { ok: false, label: "Not permitted" };
      }

    } else if (
      atype.includes("REFUND") ||
      descUp.includes("REFUND") || descUp.includes("CANCEL")
    ) {
      if (!cancellation.beforeDeparture) {
        let label;
        if (descUp.includes("NON REFUNDABLE") || descUp.includes("NON-REFUNDABLE") || descUp.includes("NO REFUND")) {
          label = "Not permitted";
          cancellation.beforeDeparture = { ok: false, label };
        } else if (descUp.includes("REFUNDABLE TICKET") || descUp.includes("FULLY REFUNDABLE") || !chargeable) {
          label = "Permitted (fee unknown)";
          cancellation.beforeDeparture = { ok: true, label };
          gdsDisclaimer = true;
        } else if (descUp.includes("REFUNDABLE")) {
          label = "Permitted (fee unknown)";
          cancellation.beforeDeparture = { ok: true, label };
          gdsDisclaimer = true;
        } else {
          label = "Fee applies (check airline)";
          cancellation.beforeDeparture = { ok: false, label };
          gdsDisclaimer = true;
        }
      }

    } else if (descUp.includes("NO-SHOW") || descUp.includes("NOSHOW")) {
      changes.noShow = { ok: false, label: "Not permitted" };
    }
  }

  // Fallback from legacy amenities shorthand
  if (!hasAmenities) {
    hasAmenities = !!(fare?.amenities?.seat || fare?.amenities?.changes || fare?.amenities?.refund);
    if (fare?.amenities?.seat) {
      const paid = /charg|paid/i.test(fare.amenities.seat);
      seats.standard = { ok: !paid, label: paid ? "Paid" : "Free" };
    }
    if (fare?.amenities?.changes) {
      const paid = /charg|paid/i.test(fare.amenities.changes);
      changes.beforeDeparture = { ok: !paid, label: paid ? "Fee applies (check airline)" : "Free" };
      if (paid) gdsDisclaimer = true;
    }
    if (fare?.amenities?.refund) {
      const nonRef = /non|no refund/i.test(fare.amenities.refund);
      cancellation.beforeDeparture = { ok: !nonRef, label: nonRef ? "Not permitted" : "Permitted (fee unknown)" };
      if (!nonRef) gdsDisclaimer = true;
    }
    if (fare?.baggage && !baggage.checked) {
      const noBag = /no bag/i.test(fare.baggage);
      baggage.checked = { ok: !noBag, label: noBag ? "Not included" : fare.baggage };
    }
  }

  return {
    fareBasis: fd0?.fareBasis || fare?.fare_basis || "",
    cabin: fd0?.cabin || "",
    fareName: fare?.fare_name || "",
    hasAmenities: hasAmenities || !!raw,
    gdsDisclaimer,
    baggage, changes, cancellation, seats,
  };
}

function buildDuffelConditions(fullOffer) {
  if (!fullOffer) return { hasAmenities: false, baggage: {}, changes: {}, cancellation: {}, seats: {} };

  const pax  = fullOffer?.slices?.[0]?.segments?.[0]?.passengers?.[0];
  const cond = fullOffer?.conditions || {};

  const baggage = {};
  const checked = pax?.baggages?.find(b => b.type === "checked" || b.type === "checked_bags");
  const carry   = pax?.baggages?.find(b => b.type === "carry_on");
  baggage.checked = checked
    ? { ok: true,  label: `${checked.quantity}× included` }
    : { ok: false, label: "Not included" };
  baggage.carryOn = carry
    ? { ok: true,  label: `${carry.quantity}× included` }
    : { ok: false, label: "Not included" };

  const fmtCond = (c) => {
    if (!c) return null;
    if (!c.allowed) return { ok: false, label: "Not permitted" };
    const amt = parseFloat(c.penalty_amount || 0);
    if (amt > 0) return { ok: "warn", label: `${c.penalty_currency || "€"}${amt.toFixed(0)} fee` };
    return { ok: true, label: "Free" };
  };

  const changes     = { beforeDeparture: fmtCond(cond.change_before_departure) };
  const cancellation = {
    beforeDeparture: fmtCond(cond.cancel_before_departure),
    refundable:      fmtCond(cond.refund_before_departure),
  };

  return {
    fareName: fullOffer?.slices?.[0]?.fare_brand_name || "",
    cabin:    pax?.cabin_class || "",
    fareBasis: "",
    hasAmenities: true,
    baggage, changes, cancellation, seats: {},
  };
}

// ─── DuffelResultCard ─────────────────────────────────────────────────────────

function DuffelResultCard({ offerGroup, index, expandedOffers, onToggle, onQuote, onSelect }) {
  const { getAuthHeaders } = useAuth();
  const offerKey = `duffel-${index}`;
  const isExpanded = expandedOffers[offerKey];
  const [activeFare, setActiveFare] = useState(0);
  const [fareRulesOpen, setFareRulesOpen] = useState(false);
  const [fareRulesData, setFareRulesData] = useState(null);
  const [fareRulesLoading, setFareRulesLoading] = useState(false);

  const { offer, summary } = offerGroup[activeFare] || offerGroup[0];

  async function handleFareRulesClick(e) {
    e.stopPropagation();
    setFareRulesOpen(true);
    if (fareRulesData) return;
    setFareRulesLoading(true);
    try {
      const resp = await axios.get(`${API}/flights/duffel-offer/${offer.id}`, { headers: getAuthHeaders() });
      setFareRulesData(buildDuffelConditions(resp.data));
    } catch {
      setFareRulesData({ hasAmenities: false, baggage: {}, changes: {}, cancellation: {}, seats: {} });
    } finally {
      setFareRulesLoading(false);
    }
  }

  return (
    <div className="border border-slate-200 rounded-lg hover:border-purple-300 transition-all bg-white overflow-hidden">
      <div className="p-5 cursor-pointer select-none" onClick={() => onToggle(offerKey)}>
        {summary.sliceSummaries.map((slice, si) => (
          <div key={si} className={si > 0 ? "mt-3 pt-3 border-t border-slate-100" : ""}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4 flex-1">
                {si === 0 && <CarrierCircle code={summary.ownerIata || summary.ownerName?.slice(0, 2)} />}
                {si > 0 && <div className="w-10" />}
                <div className="min-w-0">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-semibold text-slate-900 text-lg">{slice.depTime}</span>
                    <span className="text-slate-400">-</span>
                    <span className="font-semibold text-slate-900 text-lg">{slice.arrTime}</span>
                    {slice.nextDay && <sup className="text-xs text-purple-500 font-medium">+1</sup>}
                  </div>
                  <p className="text-sm text-slate-500 truncate">{summary.ownerName}{slice.flightNums.length > 0 && <span className="text-slate-400"> · {slice.flightNums.join(", ")}</span>}</p>
                </div>
              </div>
              <div className="text-right px-4 min-w-[100px]">
                <p className="font-semibold text-slate-900">{slice.duration}</p>
                <p className="text-xs text-slate-500">{slice.origin} - {slice.destination}</p>
              </div>
              <div className="text-right min-w-[120px]">
                <p className={`font-semibold ${slice.stops === 0 ? "text-emerald-600" : "text-slate-900"}`}>{slice.stops === 0 ? "Direct" : `${slice.stops} stop${slice.stops > 1 ? "s" : ""}`}</p>
                {slice.layovers.filter(l => l.duration).map((lay, li) => (
                  <p key={li} className="text-xs text-slate-500">{lay.duration} {lay.city}</p>
                ))}
              </div>
            </div>
          </div>
        ))}

        {/* Fare family tabs — only shown when multiple fare options exist */}
        {offerGroup.length > 1 && (
          <div className="mt-3 border-t border-slate-100 bg-slate-50 -mx-5 px-5 pt-2 pb-1">
            <div className="flex overflow-x-auto gap-1">
              {offerGroup.map((item, fi) => {
                const label = item.offer.slices?.[0]?.fare_brand_name || item.offer.slices?.[0]?.segments?.[0]?.passengers?.[0]?.cabin_class || `Fare ${fi + 1}`;
                return (
                  <button
                    key={fi}
                    onClick={(e) => { e.stopPropagation(); setActiveFare(fi); }}
                    className={`flex-shrink-0 px-3 py-1.5 rounded text-center transition-colors border ${activeFare === fi ? "border-violet-500 bg-violet-50 text-violet-700" : "border-slate-200 bg-white text-slate-500 hover:bg-slate-100"}`}
                  >
                    <div className={`text-[10px] font-semibold uppercase tracking-wide truncate max-w-[80px] ${activeFare === fi ? "text-violet-700" : "text-slate-500"}`}>{label}</div>
                    <div className={`text-sm font-bold mt-0.5 ${activeFare === fi ? "text-violet-700" : "text-slate-700"}`}>€{item.summary.totalAmount.toFixed(0)}</div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-slate-100">
          <p className="text-slate-500 text-sm">
            {offerGroup.length > 1 ? "Selected" : "From"}{" "}
            <span className="text-2xl font-bold text-slate-900 font-mono">€{summary.totalAmount.toFixed(2)}</span>
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={handleFareRulesClick}
              className="text-[10px] text-purple-600 border border-purple-200 rounded px-2 py-0.5 hover:bg-purple-50"
            >Fare Rules</button>
            <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); onQuote(offer, "duffel"); }} className="border-purple-300 text-purple-700 hover:bg-purple-50">Send Quote</Button>
            <Button size="sm" onClick={(e) => { e.stopPropagation(); onSelect(offer, "duffel"); }} className="bg-slate-900 hover:bg-slate-800 text-white">Select</Button>
            {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
          </div>
        </div>
      </div>
      {isExpanded && (
        <div className="border-t border-slate-200 bg-slate-50 p-5 space-y-5">
          {offer.slices?.map((slice, sliceIdx) => (
            <div key={sliceIdx}>
              {sliceIdx > 0 && <div className="border-t border-slate-200 mb-4" />}
              {slice.segments?.map((segment, segIdx) => (
                <div key={segIdx}>
                  {segIdx > 0 && (() => {
                    const connTime = calcConnectionTime(slice.segments[segIdx - 1].arriving_at, segment.departing_at);
                    return connTime ? (
                      <div className="flex items-center justify-center py-3">
                        <div className="bg-amber-50 border border-amber-200 text-amber-700 text-sm font-medium px-4 py-2 rounded-full flex items-center gap-2">
                          <Timer className="w-4 h-4" />
                          {connTime} layover at {segment.origin?.name || getAirportName(segment.origin?.iata_code)} ({segment.origin?.iata_code})
                        </div>
                      </div>
                    ) : null;
                  })()}
                  <div className="flex gap-4 py-3">
                    <div className="flex flex-col items-center pt-1">
                      <div className="w-3 h-3 rounded-full border-2 border-slate-400 bg-white" />
                      <div className="w-px flex-1 bg-slate-300 my-1" />
                      <div className="w-3 h-3 rounded-full border-2 border-slate-400 bg-white" />
                    </div>
                    <div className="flex-1 space-y-4">
                      <div>
                        <p className="font-semibold text-slate-900">
                          {fmtDate(segment.departing_at)}, {fmtTime(segment.departing_at)}
                          <span className="font-normal text-slate-600 ml-2">Depart from {segment.origin?.name || getAirportName(segment.origin?.iata_code)} ({segment.origin?.iata_code})</span>
                        </p>
                      </div>
                      <p className="text-sm text-slate-500 pl-2">Flight duration: {parseDuration(segment.duration)}</p>
                      <div>
                        <p className="font-semibold text-slate-900">
                          {fmtDate(segment.arriving_at)}, {fmtTime(segment.arriving_at)}
                          <span className="font-normal text-slate-600 ml-2">Arrive at {segment.destination?.name || getAirportName(segment.destination?.iata_code)} ({segment.destination?.iata_code})</span>
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 bg-white rounded-lg p-3 border border-slate-100">
                        <span className="capitalize font-medium">{segment.passengers?.[0]?.cabin_class || "Economy"}</span>
                        <span>{segment.operating_carrier?.name || segment.marketing_carrier?.name}</span>
                        {segment.aircraft?.name && <span>{segment.aircraft.name}</span>}
                        <span className="font-mono font-semibold">{segment.marketing_carrier?.iata_code}{segment.marketing_carrier_flight_number}</span>
                        {segment.passengers?.[0]?.baggages?.map((b, bi) => (
                          <span key={bi} className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{b.quantity} {b.type === "carry_on" ? "carry-on" : "checked"} bag{b.quantity > 1 ? "s" : ""}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      <FareConditionsModal
        open={fareRulesOpen}
        onClose={() => setFareRulesOpen(false)}
        title={fareRulesData?.fareName || offer.slices?.[0]?.fare_brand_name || offer.slices?.[0]?.segments?.[0]?.passengers?.[0]?.cabin_class || "Fare"}
        route={`${summary.sliceSummaries[0]?.origin || ""} → ${summary.sliceSummaries[0]?.destination || ""}`}
        fareData={fareRulesData}
        loading={fareRulesLoading}
      />
    </div>
  );
}

// ─── ManualDealCard ───────────────────────────────────────────────────────────

function ManualDealCard({ deal, index, expandedOffers, onToggle, onQuote, onSelect, onDelete, onShare, onEdit }) {
  const dealKey = `manual-${index}`;
  const isExpanded = expandedOffers[dealKey];
  const segs = getDealSegments(deal);

  // Group segments by trip legs (>24h gap = separate leg)
  const legGroups = [];
  let currentGroup = [segs[0]];
  for (let i = 1; i < segs.length; i++) {
    const diff = segs[i-1].arrival_time && segs[i].departure_time
      ? new Date(segs[i].departure_time) - new Date(segs[i-1].arrival_time) : 0;
    if (diff > 24 * 3600000) { legGroups.push(currentGroup); currentGroup = [segs[i]]; }
    else currentGroup.push(segs[i]);
  }
  legGroups.push(currentGroup);

  return (
    <div className="border border-emerald-200 rounded-lg hover:border-emerald-400 transition-all bg-white overflow-hidden">
      <div className="p-4 cursor-pointer select-none" onClick={() => onToggle(dealKey)}>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0">
              <Plane className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-emerald-900">{deal.airline}</p>
              {legGroups.map((group, gi) => {
                const first = group[0], last = group[group.length - 1];
                const depDate = first.departure_time ? fmtShortDate(first.departure_time) : "";
                const depTime = first.departure_time ? fmtTime(first.departure_time) : "--:--";
                const arrTime = last.arrival_time ? fmtTime(last.arrival_time) : "--:--";
                const stops = group.length - 1;
                return (
                  <div key={gi} className={`flex items-center gap-2 text-sm ${gi > 0 ? "mt-1" : ""}`}>
                    <span className="font-mono font-bold text-slate-800">{first.departure_airport}</span>
                    <span className="text-slate-400">→</span>
                    <span className="font-mono font-bold text-slate-800">{last.arrival_airport}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-600">{depDate}</span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-600">{depTime} – {arrTime}</span>
                    {stops > 0 && <Badge className="text-[10px] bg-amber-100 text-amber-700 px-1">{stops} stop{stops > 1 ? "s" : ""}</Badge>}
                  </div>
                );
              })}
              <div className="flex items-center gap-2 mt-1 text-xs text-emerald-600">
                <Briefcase className="w-3 h-3" />
                <span>{deal.baggage_included}</span>
                <span className="text-slate-300">·</span>
                <span className="capitalize">{deal.cabin_class}</span>
              </div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-2xl font-bold font-mono text-emerald-900">€{deal.price.toFixed(2)}</p>
            {isExpanded ? <ChevronUp className="w-4 h-4 text-emerald-400 ml-auto mt-1" /> : <ChevronDown className="w-4 h-4 text-emerald-400 ml-auto mt-1" />}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-emerald-100 bg-emerald-50/30 p-4 space-y-0">
          {segs.map((seg, segIdx) => {
            const depDate = seg.departure_time ? fmtShortDate(seg.departure_time) : "";
            const depTime = seg.departure_time ? fmtTime(seg.departure_time) : "--:--";
            const arrDate = seg.arrival_time ? fmtShortDate(seg.arrival_time) : "";
            const arrTime = seg.arrival_time ? fmtTime(seg.arrival_time) : "--:--";
            const connTime = segIdx > 0 ? calcConnectionTime(segs[segIdx - 1].arrival_time, seg.departure_time) : null;
            return (
              <div key={segIdx}>
                {segIdx > 0 && connTime && (
                  <div className="flex items-center justify-center py-2">
                    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium px-3 py-1.5 rounded-full">
                      <Timer className="w-3 h-3" />Connection: {connTime} at {getAirportCity(seg.departure_airport)} ({seg.departure_airport})
                    </div>
                  </div>
                )}
                {segIdx > 0 && !connTime && (
                  <div className="flex items-center gap-3 py-3">
                    <div className="flex-1 border-t border-dashed border-emerald-300" />
                    <span className="text-xs text-emerald-500 font-medium px-2">Return / Next Leg</span>
                    <div className="flex-1 border-t border-dashed border-emerald-300" />
                  </div>
                )}
                <div className="bg-white rounded-lg border border-emerald-100 p-4 mb-2">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <span className="font-mono font-bold text-slate-700">{seg.flight_number || "—"}</span>
                      {seg.operated_by && <><span>·</span><span>{seg.operated_by}</span></>}
                      <span>·</span><span className="capitalize">{deal.cabin_class}</span>
                    </div>
                    <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">Flight {segIdx + 1}</Badge>
                  </div>
                  <div className="flex items-stretch gap-4">
                    <div className="text-center min-w-[90px]">
                      <p className="text-2xl font-bold font-mono text-slate-900">{depTime}</p>
                      <p className="text-xs font-medium text-slate-500 mt-0.5">{depDate}</p>
                      <p className="text-xs font-mono font-bold text-emerald-700 mt-1">{seg.departure_airport}</p>
                      <p className="text-[10px] text-slate-400 leading-tight">{getAirportName(seg.departure_airport)}</p>
                    </div>
                    <div className="flex flex-col items-center justify-center flex-1 py-1">
                      <div className="w-full flex items-center gap-1">
                        <div className="flex-1 h-px bg-emerald-300" />
                        <Plane className="w-4 h-4 text-emerald-500 rotate-90 shrink-0" />
                        <div className="flex-1 h-px bg-emerald-300" />
                      </div>
                    </div>
                    <div className="text-center min-w-[90px]">
                      <p className="text-2xl font-bold font-mono text-slate-900">{arrTime}</p>
                      <p className="text-xs font-medium text-slate-500 mt-0.5">{arrDate}</p>
                      <p className="text-xs font-mono font-bold text-emerald-700 mt-1">{seg.arrival_airport}</p>
                      <p className="text-[10px] text-slate-400 leading-tight">{getAirportName(seg.arrival_airport)}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
          {deal.notes && (
            <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 p-2 rounded italic mt-2">📝 {deal.notes}</p>
          )}
          <div className="flex justify-end gap-2 pt-3 flex-wrap">
            {onDelete && (
              <Button size="sm" variant="outline" onClick={() => onDelete(deal.id)} className="border-red-300 text-red-600 hover:bg-red-50">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />Delete
              </Button>
            )}
            {onEdit && (
              <Button size="sm" variant="outline" onClick={() => onEdit(deal)} className="border-blue-300 text-blue-700 hover:bg-blue-50">
                <Pencil className="w-3.5 h-3.5 mr-1.5" />Edit Deal
              </Button>
            )}
            {onShare && (
              <Button size="sm" variant="outline" onClick={() => onShare(deal)} className="border-green-300 text-green-700 hover:bg-green-50">
                <Share2 className="w-3.5 h-3.5 mr-1.5" />Share with Customer
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => onQuote(deal, "manual")} className="border-purple-300 text-purple-700 hover:bg-purple-50">
              <FileText className="w-3.5 h-3.5 mr-1.5" />Send Quote
            </Button>
            <Button size="sm" onClick={() => onSelect(deal, "manual")} className="btn-accent">
              <CheckCircle className="w-4 h-4 mr-2" />Select & Book
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Filter helpers ───────────────────────────────────────────────────────────

function applyAmadeusFilters(results, filters) {
  let out = [...results];
  if (filters.stops !== "any") out = out.filter(r => filters.stops === "0" ? r.stops === 0 : filters.stops === "1" ? r.stops === 1 : r.stops >= 2);
  if (filters.maxPrice) out = out.filter(r => r.price <= filters.maxPrice);
  if (filters.selectedAirlines.size) out = out.filter(r => filters.selectedAirlines.has(r.validating_airline || r.segments?.[0]?.carrier));
  if (filters.transitAirports?.size) out = out.filter(r => {
    const allSegs = [...(r.segments || []), ...(r.return_segments || [])];
    const layovers = allSegs.slice(0, -1).map(s => s.arrival_airport).filter(Boolean);
    return layovers.some(a => filters.transitAirports.has(a));
  });
  const depTime = r => r.segments?.[0]?.departure_time || "";
  if (filters.sort === "fastest")  out.sort((a, b) => (parseDurMins(a.total_duration) || 9999) - (parseDurMins(b.total_duration) || 9999));
  else if (filters.sort === "earliest") out.sort((a, b) => depTime(a).localeCompare(depTime(b)));
  else if (filters.sort === "latest")   out.sort((a, b) => depTime(b).localeCompare(depTime(a)));
  else out.sort((a, b) => (a.price || 0) - (b.price || 0));
  return out;
}

function applyGoogleFilters(results, filters) {
  let out = [...results];
  if (filters.stops !== "any") out = out.filter(r => filters.stops === "0" ? r.stops === 0 : filters.stops === "1" ? r.stops === 1 : r.stops >= 2);
  if (filters.maxPrice) out = out.filter(r => r.price <= filters.maxPrice);
  if (filters.selectedAirlines.size) out = out.filter(r => filters.selectedAirlines.has(r.airline));
  if (filters.transitAirports?.size) out = out.filter(r => {
    const layovers = (r.legs || []).slice(0, -1).map(l => l.arrival_airport).filter(Boolean);
    return layovers.some(a => filters.transitAirports.has(a));
  });
  if (filters.sort === "fastest")  out.sort((a, b) => (a.total_duration || 9999) - (b.total_duration || 9999));
  else if (filters.sort === "earliest") out.sort((a, b) => (a.departure_time || "").localeCompare(b.departure_time || ""));
  else if (filters.sort === "latest")   out.sort((a, b) => (b.departure_time || "").localeCompare(a.departure_time || ""));
  else out.sort((a, b) => (a.price || 0) - (b.price || 0));
  return out;
}

function applyDuffelFilters(offersWithSummary, filters) {
  let out = [...offersWithSummary];
  if (filters.stops !== "any") out = out.filter(({ summary }) => filters.stops === "0" ? summary.totalStops === 0 : filters.stops === "1" ? summary.totalStops === 1 : summary.totalStops >= 2);
  if (filters.maxPrice) out = out.filter(({ summary }) => summary.totalAmount <= filters.maxPrice);
  if (filters.selectedAirlines.size) out = out.filter(({ summary }) => filters.selectedAirlines.has(summary.ownerName));
  if (filters.transitAirports?.size) out = out.filter(({ offer }) => {
    const layovers = (offer.slices || []).flatMap(sl =>
      (sl.segments || []).slice(0, -1).map(s => s.destination?.iata_code).filter(Boolean)
    );
    return layovers.some(a => filters.transitAirports.has(a));
  });
  const depAt = ({ offer }) => offer.slices?.[0]?.segments?.[0]?.departing_at || "";
  if (filters.sort === "fastest")  out.sort((a, b) => a.summary.totalDurationMin - b.summary.totalDurationMin);
  else if (filters.sort === "earliest") out.sort((a, b) => depAt(a).localeCompare(depAt(b)));
  else if (filters.sort === "latest")   out.sort((a, b) => depAt(b).localeCompare(depAt(a)));
  else out.sort((a, b) => a.summary.totalAmount - b.summary.totalAmount);
  return out;
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function FlightSearchPanel({
  request,
  // Amadeus/Google internal (manage own state)
  onNewDeal,
  onAddLeg,
  // Duffel (parent manages)
  duffelOffers = [],
  duffelLoading = false,
  onDuffelSearch,
  // Manual deals (parent manages)
  manualDeals = [],
  onQuote,
  onSelectOffer,
  onDeleteDeal,
  onAddManualDeal,
  onEditDeal,
}) {
  const [shareDeal, setShareDeal] = useState(null);
  const { getAuthHeaders } = useAuth();

  const [amadeusEnv, setAmadeusEnv] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/amadeus-status`, { headers: getAuthHeaders() })
      .then(r => setAmadeusEnv(r.data.environment))
      .catch(() => {});
  }, []);

  const [amadeusResults, setAmadeusResults] = useState([]);
  const [segmentResults, setSegmentResults] = useState({});
  const [gridData,       setGridData]       = useState([]);

  const [amadeusLoading, setAmadeusLoading] = useState(false);
  const [googleLoading,  setGoogleLoading]  = useState(false);
  const [gridLoading,    setGridLoading]    = useState(false);

  const [selectedDates,        setSelectedDates]        = useState({});
  const [amadeusSelectedDates, setAmadeusSelectedDates] = useState({});

  const [activeTab, setActiveTab] = useState("amadeus");

  const [usage, setUsage] = useState(null);

  const defaultFilters = { stops: "any", maxPrice: null, sort: "cheapest", selectedAirlines: new Set(), transitAirports: new Set() };
  const [amadeusFilters, setAmadeusFilters] = useState(defaultFilters);
  const [googleFilters,  setGoogleFilters]  = useState(defaultFilters);
  const [duffelFilters,  setDuffelFilters]  = useState(defaultFilters);

  const [expandedOffers, setExpandedOffers] = useState({});

  const travelType  = request?.travel_type || "one_way";
  const isReturn    = travelType === "return";
  const isMultiCity = travelType === "multi_city";
  const cabin       = request?.travel_class || "economy";
  const adults      = request?.passengers?.adults   || 1;
  const children    = request?.passengers?.children || 0;
  const infants     = request?.passengers?.infants  || 0;
  const paxSummary  = `${adults}A${children > 0 ? ` ${children}C` : ""}${infants > 0 ? ` ${infants}I` : ""}`;
  const [legs, setLegs] = useState([]);

  const segmentsKey = JSON.stringify(request?.segments?.map(s => `${s.departure_airport}-${s.arrival_airport}-${s.departure_date}`));

  useEffect(() => {
    const builtLegs = buildLegsFromRequest(request);
    setLegs(builtLegs);
    const dates = {};
    builtLegs.forEach((leg, i) => { dates[i] = leg.date; });
    setSelectedDates(dates);
    setAmadeusSelectedDates(dates);
    setSegmentResults({});
    setAmadeusResults([]);
    setGridData([]);
    fetchUsage();
  }, [request?.id, segmentsKey, request?.travel_type]);

  const fetchUsage = async () => {
    try {
      const res = await axios.get(`${API}/flights/search-usage`, { headers: getAuthHeaders() });
      setUsage(res.data);
    } catch (e) {}
  };

  const runAmadeusSearch = async (dates = amadeusSelectedDates) => {
    if (legs.length === 0) return;
    setAmadeusLoading(true);
    setAmadeusResults([]);
    try {
      const firstLeg  = legs[0];
      const returnLeg = isReturn && legs[1] ? legs[1] : null;
      const res = await axios.post(`${API}/flights/search-amadeus`, {
        origin: firstLeg.origin, destination: firstLeg.destination,
        departure_date: dates[0] || firstLeg.date,
        return_date: returnLeg ? (dates[1] || returnLeg.date) : null,
        adults, children, infants, travel_class: cabin, max_results: 15,
      }, { headers: getAuthHeaders() });
      const results = res.data.results || [];
      setAmadeusResults(results);
      if (results.length > 0) setActiveTab("amadeus");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Amadeus search failed");
    } finally { setAmadeusLoading(false); }
  };

  const runGoogleSearch = async (dates = selectedDates) => {
    if (legs.length === 0) return;
    if (usage?.exhausted) { toast.error("Monthly Google Flights limit reached"); return; }
    setGoogleLoading(true);
    setSegmentResults({});
    try {
      const promises = legs.map((leg, i) =>
        axios.post(`${API}/flights/search-google`, {
          origin: leg.origin, destination: leg.destination,
          outbound_date: dates[i] || leg.date, return_date: null,
          cabin_class: cabin, adults, children, infants,
        }, { headers: getAuthHeaders() }).then(r => {
          if (r.data.usage) setUsage(prev => ({ ...prev, ...r.data.usage }));
          return r.data.results || [];
        })
      );
      const allResults = await Promise.all(promises);
      const newResults = {};
      allResults.forEach((res, i) => { newResults[i] = res; });
      setSegmentResults(newResults);
      const total = allResults.reduce((sum, r) => sum + r.length, 0);
      if (total === 0) toast.info("No Google Flights results for these dates");
      await fetchUsage();
    } catch (err) {
      if (err.response?.status === 429) {
        toast.error("Monthly Google Flights limit reached");
        setUsage(prev => ({ ...prev, exhausted: true, remaining: 0 }));
      } else {
        toast.error(err.response?.data?.detail || "Google search failed");
      }
    } finally { setGoogleLoading(false); }
  };

  const runGridSearch = async () => {
    if (legs.length === 0) return;
    const firstLeg = legs[0];
    setGridLoading(true);
    setGridData([]);
    setActiveTab("grid");
    try {
      const res = await axios.post(`${API}/flights/search-grid`, {
        origin: firstLeg.origin, destination: firstLeg.destination,
        outbound_date: selectedDates[0] || firstLeg.date,
        cabin_class: cabin, adults, children, infants, date_range: 3,
      }, { headers: getAuthHeaders() });
      setGridData(res.data.grid || []);
      if (res.data.usage) setUsage(prev => ({ ...prev, ...res.data.usage }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Grid search failed");
    } finally { setGridLoading(false); }
  };

  const handleSearchAll = async () => {
    if (legs.length === 0) { toast.error("No segments found"); return; }
    await Promise.allSettled([runAmadeusSearch(), runGoogleSearch()]);
  };

  const toggleExpand = (key) => setExpandedOffers(prev => ({ ...prev, [key]: !prev[key] }));

  // Filtered results
  const filteredAmadeus = applyAmadeusFilters(amadeusResults, amadeusFilters);
  const allGoogleFlat   = Object.values(segmentResults).flat();
  const filteredGoogle  = Object.fromEntries(
    Object.entries(segmentResults).map(([i, res]) => [i, applyGoogleFilters(res, googleFilters)])
  );
  const amadeusAirlines = [...new Set(amadeusResults.map(r => r.validating_airline || r.segments?.[0]?.carrier).filter(Boolean))];
  const amadeusTransitAirports = [...new Set(
    amadeusResults.filter(r => r.stops > 0).flatMap(r =>
      [...(r.segments || []), ...(r.return_segments || [])].slice(0, -1).map(s => s.arrival_airport).filter(Boolean)
    )
  )].sort();
  const googleAirlines  = [...new Set(allGoogleFlat.map(r => r.airline).filter(Boolean))];
  const googleTransitAirports = [...new Set(
    allGoogleFlat.filter(r => r.stops > 0).flatMap(r => (r.legs || []).slice(0, -1).map(l => l.arrival_airport).filter(Boolean))
  )].sort();
  const googleTotal     = Object.values(segmentResults).reduce((s, r) => s + r.length, 0);
  const cheapestGrid    = gridData.length ? Math.min(...gridData.filter(d => d.price).map(d => d.price)) : null;
  const anyLoading      = amadeusLoading || googleLoading;

  // Duffel filtered summary
  const duffelWithSummary  = duffelOffers.map(o => ({ offer: o, summary: getOfferSummary(o) }));
  const duffelAirlines     = [...new Set(duffelWithSummary.map(({ summary }) => summary.ownerName).filter(Boolean))];
  const duffelTransitAirports = [...new Set(
    duffelWithSummary.filter(({ summary }) => summary.totalStops > 0).flatMap(({ offer }) =>
      (offer.slices || []).flatMap(sl =>
        (sl.segments || []).slice(0, -1).map(s => s.destination?.iata_code).filter(Boolean)
      )
    )
  )].sort();
  const filteredDuffel     = applyDuffelFilters(duffelWithSummary, duffelFilters);

  // Group Duffel offers by flight fingerprint so fare families appear as tabs
  const duffelGrouped = (() => {
    const groups = new Map();
    for (const item of filteredDuffel) {
      const seg0 = item.offer.slices?.[0]?.segments?.[0];
      const fingerprint = [
        seg0?.departing_at?.slice(0, 16) || "",
        seg0?.marketing_carrier?.iata_code || seg0?.operating_carrier?.iata_code || "",
        seg0?.marketing_carrier_flight_number || "",
        item.offer.slices?.length || 1,
      ].join("|");
      if (!groups.has(fingerprint)) groups.set(fingerprint, []);
      groups.get(fingerprint).push(item);
    }
    return [...groups.values()];
  })();

  const tabs = [
    { key: "amadeus",  label: "Amadeus",       count: amadeusResults.length, color: "purple",  loading: amadeusLoading },
    { key: "google",   label: "Google Flights", count: googleTotal,           color: "blue",    loading: googleLoading },
    { key: "duffel",   label: "Duffel",         count: duffelOffers.length,   color: "violet",  loading: duffelLoading },
    { key: "manual",   label: "Manual Deals",   count: manualDeals.length,    color: "emerald", loading: false },
    { key: "grid",     label: "±3 Day Grid",    count: gridData.filter(d => d.price).length, color: "gray", loading: gridLoading },
  ];

  return (
    <div className="flex flex-col h-full">
      {/* ── Production Amadeus warning ────────────────────────────────── */}
      {amadeusEnv === "production" && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 text-xs font-medium">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 text-amber-600" />
          Production Amadeus API — live fares &amp; orders. Bookings create real PNRs and incur charges.
        </div>
      )}
      {/* ── Search bar ───────────────────────────────────────────────────── */}
      <div className="p-4 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-white">
        {/* Route summary */}
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <PlaneTakeoff className="w-4 h-4 text-purple-600 shrink-0" />
          <div className="flex items-center gap-1.5 flex-wrap">
            {legs.map((leg, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <span className="text-gray-300 mx-0.5">|</span>}
                <span className="font-mono font-bold text-purple-800 text-sm">{leg.origin}</span>
                <span className="text-gray-400 text-xs">→</span>
                <span className="font-mono font-bold text-purple-800 text-sm">{leg.destination}</span>
              </span>
            ))}
          </div>
          <span className="text-gray-300">·</span>
          <span className="text-xs text-gray-500 capitalize">{cabin}</span>
          <span className="text-gray-300">·</span>
          <span className="text-xs text-gray-500">{paxSummary}</span>
          <Badge className="text-xs capitalize bg-purple-100 text-purple-700 border-purple-200">{travelType.replace("_", " ")}</Badge>
        </div>

        {/* Date adjusters */}
        <div className="space-y-2 mb-3">
          {legs.map((leg, i) => (
            <DateSelector
              key={`${leg.origin}-${leg.destination}-${i}`}
              label={`${leg.label}: ${leg.origin}→${leg.destination}`}
              baseDate={leg.date}
              selectedDate={selectedDates[i] || leg.date}
              onSelect={d => {
                const next = { ...selectedDates, [i]: d };
                setSelectedDates(next);
                setAmadeusSelectedDates(next);
                setSegmentResults(prev => { const n = { ...prev }; delete n[i]; return n; });
              }}
            />
          ))}
        </div>

        {/* Actions row */}
        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={runAmadeusSearch} disabled={amadeusLoading} className="bg-purple-600 hover:bg-purple-700 text-white h-9 px-4">
            {amadeusLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching...</> : <><Search className="w-4 h-4 mr-2" />Search Amadeus</>}
          </Button>

          <Button onClick={runGoogleSearch} disabled={googleLoading} className="bg-purple-600 hover:bg-purple-700 text-white h-9 px-4">
            {googleLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching...</> : <><Search className="w-4 h-4 mr-2" />Search Google</>}
          </Button>

          {onDuffelSearch && (
            <Button variant="outline" onClick={onDuffelSearch} disabled={duffelLoading} className="h-9 border-violet-300 text-violet-700 hover:bg-violet-50 px-4">
              {duffelLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Searching...</> : <><Search className="w-4 h-4 mr-2" />Search Duffel</>}
            </Button>
          )}

          <Button variant="outline" size="sm" onClick={runGridSearch} disabled={gridLoading || (usage && usage.remaining < 7)} className="h-9 border-gray-300 text-gray-600 hover:bg-gray-50">
            {gridLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Zap className="w-3 h-3 mr-1" />}
            ±3 Day Grid
          </Button>

          {legs[0] && (
            <GoogleFlightsLink
              origin={legs[0].origin} destination={legs[0].destination}
              date={selectedDates[0] || legs[0].date}
              returnDate={isReturn && legs[1] ? (selectedDates[1] || legs[1].date) : null}
              cabin={cabin} />
          )}

          {onAddManualDeal && (
            <Button variant="outline" size="sm" onClick={onAddManualDeal} className="h-9 border-emerald-300 text-emerald-700 hover:bg-emerald-50 ml-auto">
              <Plus className="w-3 h-3 mr-1" />Add Manual Deal
            </Button>
          )}

          <div className={onAddManualDeal ? "" : "ml-auto"}>
            <UsageBadge usage={usage} />
          </div>
        </div>

      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div className="flex gap-1 flex-wrap border-b border-gray-100 px-4 pt-3 pb-0 bg-white">
        {tabs.map(({ key, label, count, color, loading: tabLoading }) => (
          <button key={key} onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-t-lg text-sm font-medium transition-colors border-b-2 -mb-px ${
              activeTab === key
                ? color === "purple" ? "border-purple-600 text-purple-700 bg-purple-50"
                : color === "blue"   ? "border-blue-600 text-blue-700 bg-blue-50"
                : color === "violet" ? "border-violet-600 text-violet-700 bg-violet-50"
                : color === "emerald" ? "border-emerald-600 text-emerald-700 bg-emerald-50"
                : "border-gray-700 text-gray-700 bg-gray-50"
                : "border-transparent text-gray-500 hover:bg-gray-50"
            }`}>
            {label}
            {(count > 0 || tabLoading) && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === key ? "bg-white/80" : "bg-gray-100 text-gray-600"}`}>
                {tabLoading ? <Loader2 className="w-3 h-3 animate-spin inline" /> : count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Filter bar (always visible, tab-specific) ─────────────────── */}
      {activeTab === "amadeus" && amadeusResults.length > 0 && (
        <FilterBar filters={amadeusFilters} onChange={setAmadeusFilters} airlines={amadeusAirlines} showTransit transitAirports={amadeusTransitAirports} />
      )}
      {activeTab === "google" && googleTotal > 0 && (
        <FilterBar filters={googleFilters} onChange={setGoogleFilters} airlines={googleAirlines} showTransit transitAirports={googleTransitAirports} />
      )}
      {activeTab === "duffel" && duffelOffers.length > 0 && (
        <FilterBar filters={duffelFilters} onChange={setDuffelFilters} airlines={duffelAirlines} showTransit transitAirports={duffelTransitAirports} />
      )}

      {/* ── Results area ─────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">

        {/* AMADEUS TAB */}
        {activeTab === "amadeus" && (
          <div className="space-y-3">
            {amadeusEnv !== "production" && (
              <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                <AlertTriangle className="w-3 h-3 shrink-0" /> Amadeus is in test mode — results are synthetic
              </div>
            )}
            {amadeusResults.length === 0 && !amadeusLoading && (
              <div className="text-center py-10 text-gray-400">
                <PlaneTakeoff className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Click "Search Amadeus" to fetch Amadeus fares</p>
              </div>
            )}
            {filteredAmadeus.length === 0 && amadeusResults.length > 0 && (
              <p className="text-xs text-gray-400 text-center py-4">No results match the current filters.</p>
            )}
            <div className="space-y-3">
              {filteredAmadeus.map((result, j) => (
                <AmadeusResultCard key={result.offer_id || j} result={result} index={j} onNewDeal={onNewDeal} request={request} />
              ))}
            </div>
          </div>
        )}

        {/* GOOGLE FLIGHTS TAB */}
        {activeTab === "google" && (
          <div className="space-y-4">
            {isReturn && (
              <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                ℹ Searched as 2 one-way legs for flexibility. Add prices for total estimate.
              </div>
            )}
            {googleTotal === 0 && !googleLoading && (
              <div className="text-center py-10 text-gray-400">
                <PlaneTakeoff className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Click "Search Google" to fetch Google Flights fares</p>
                {usage && <UsageBadge usage={usage} />}
              </div>
            )}
            {legs.map((leg, i) => {
              const res = filteredGoogle[i];
              if (!res || res.length === 0) return null;
              const colors = { 0: "blue", 1: "purple", 2: "emerald", 3: "orange" };
              const c = colors[i] || "gray";
              return (
                <div key={i} className="space-y-2">
                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg bg-${c}-50 border border-${c}-200`}>
                    <p className={`text-xs font-semibold text-${c}-700 uppercase tracking-wide`}>✈ {leg.label}: {leg.origin} → {leg.destination} ({selectedDates[i] || leg.date})</p>
                    <span className={`text-xs text-${c}-500`}>{res.length} options</span>
                  </div>
                  <div className="space-y-2">
                    {res.map((flight, j) => <GoogleFlightCard key={j} flight={flight} index={j} onNewDeal={onNewDeal} onAddLeg={onAddLeg} />)}
                  </div>
                </div>
              );
            })}
            {isReturn && Object.keys(segmentResults).length === 2 && (() => {
              const outP = segmentResults[0]?.[0]?.price;
              const retP = segmentResults[1]?.[0]?.price;
              if (!outP || !retP) return null;
              return (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl text-sm">
                  <p className="text-blue-800 font-medium">Cheapest combined estimate</p>
                  <p className="text-blue-600 text-xs mt-1">Outbound €{outP} + Return €{retP} = <span className="font-bold text-base">€{outP + retP}</span></p>
                  <p className="text-blue-400 text-xs mt-0.5">Actual may differ when booked as a return ticket</p>
                </div>
              );
            })()}
            {usage && !usage.exhausted && googleTotal > 0 && (
              <p className="text-xs text-gray-400">{usage.remaining} Google searches remaining this month</p>
            )}
          </div>
        )}

        {/* DUFFEL TAB */}
        {activeTab === "duffel" && (
          <div className="space-y-3">
            {duffelOffers.length === 0 && !duffelLoading && (
              <div className="text-center py-12">
                <Search className="w-12 h-12 text-violet-200 mx-auto mb-4" />
                <p className="text-violet-500">No Duffel offers yet</p>
                <p className="text-sm text-violet-400 mt-1 mb-4">Click "Duffel" in the search bar to find flights</p>
                {onDuffelSearch && (
                  <Button onClick={onDuffelSearch} disabled={duffelLoading} className="bg-violet-600 hover:bg-violet-700 text-white">
                    {duffelLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
                    Search Duffel
                  </Button>
                )}
              </div>
            )}
            {duffelLoading && (
              <div className="text-center py-8"><Loader2 className="w-8 h-8 text-violet-400 mx-auto animate-spin" /></div>
            )}
            {filteredDuffel.length === 0 && duffelOffers.length > 0 && (
              <p className="text-xs text-gray-400 text-center py-4">No results match the current filters.</p>
            )}
            {duffelGrouped.map((group, index) => (
              <DuffelResultCard
                key={group[0].offer.id || index}
                offerGroup={group} index={index}
                expandedOffers={expandedOffers} onToggle={toggleExpand}
                onQuote={onQuote || (() => {})}
                onSelect={onSelectOffer || (() => {})}
              />
            ))}
          </div>
        )}

        {/* MANUAL DEALS TAB */}
        {activeTab === "manual" && (
          <div className="space-y-3">
            {manualDeals.length === 0 && (
              <div className="text-center py-12">
                <Plus className="w-12 h-12 text-emerald-200 mx-auto mb-4" />
                <p className="text-emerald-500">No manual deals added yet</p>
                <p className="text-sm text-emerald-400 mt-1 mb-4">Add deals you find from other sources</p>
                {onAddManualDeal && (
                  <Button onClick={onAddManualDeal} variant="outline" className="border-emerald-400 text-emerald-700 hover:bg-emerald-50">
                    <Plus className="w-4 h-4 mr-2" />Add Manual Deal
                  </Button>
                )}
              </div>
            )}
            {manualDeals.map((deal, index) => (
              <ManualDealCard
                key={deal.id}
                deal={deal} index={index}
                expandedOffers={expandedOffers} onToggle={toggleExpand}
                onQuote={onQuote || (() => {})}
                onSelect={onSelectOffer || (() => {})}
                onDelete={onDeleteDeal}
                onShare={setShareDeal}
                onEdit={onEditDeal}
              />
            ))}
            <DealShareModal
              deal={shareDeal}
              open={!!shareDeal}
              onClose={() => setShareDeal(null)}
            />
          </div>
        )}

        {/* GRID TAB */}
        {activeTab === "grid" && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500">
              Cheapest fare for {legs[0]?.origin}→{legs[0]?.destination} across ±3 days from requested date. Uses 7 credits.
              {isMultiCity && " (Grid only searches first segment)"}
            </p>
            {usage && !usage.exhausted && usage.remaining < 7 && (
              <div className="flex items-center gap-2 text-xs text-amber-600 bg-amber-50 p-2 rounded">
                <AlertTriangle className="w-3 h-3" /> Only {usage.remaining} searches left
              </div>
            )}
            {gridData.length === 0 && !gridLoading && (
              <div className="text-center py-10 text-gray-400">
                <Zap className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Click "±3 Day Grid" to search across dates</p>
              </div>
            )}
            {gridData.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-7 gap-1.5">
                  {gridData.map((day, i) => {
                    const isCheapest = day.price === cheapestGrid && day.price !== null;
                    return (
                      <button key={i}
                        onClick={() => { setSelectedDates(p => ({ ...p, 0: day.date })); setAmadeusSelectedDates(p => ({ ...p, 0: day.date })); setActiveTab("amadeus"); }}
                        disabled={!day.price}
                        className={`p-2 rounded-xl border text-center transition-all ${isCheapest ? "border-green-400 bg-green-50 shadow-sm" : day.is_base_date ? "border-purple-300 bg-purple-50" : "border-gray-200 bg-white"} ${day.price ? "hover:border-purple-400 hover:shadow-md cursor-pointer" : "opacity-40 cursor-not-allowed"}`}>
                        <div className="text-[10px] text-gray-400">{day.delta === 0 ? "Req" : day.delta > 0 ? `+${day.delta}` : day.delta}</div>
                        <div className="text-[10px] text-gray-400">{day.date?.slice(5)}</div>
                        <div className={`text-xs font-bold mt-1 ${isCheapest ? "text-green-700" : "text-gray-800"}`}>{day.price ? formatPrice(day.price) : "N/A"}</div>
                        {isCheapest && <div className="text-[9px] text-green-600 font-bold">BEST</div>}
                        {day.airline && <div className="text-[9px] text-gray-400 truncate">{day.airline}</div>}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400">Click any date to set it and search Amadeus</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
